// Utility functions for Shadow DOM integration

import createCache from '@emotion/cache';

export interface ShadowDOMConfig {
  mode?: 'open' | 'closed';
  styles?: string[];
  containerId?: string;
}

export const createShadowDOMContainer = (
  hostElement: Element, 
  config: ShadowDOMConfig = {}
): { shadowRoot: ShadowRoot; container: HTMLElement } => {
  const { mode = 'closed', containerId = 'chat-widget-shadow-container' } = config;
  
  // Create Shadow DOM root
  const shadowRoot = hostElement.attachShadow({ mode });
  
  // Create container with proper styling - FIXED: Remove full width/height to prevent overlay
  const container = document.createElement('div');
  container.id = containerId;
  container.style.cssText = `
    all: initial;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    box-sizing: border-box;
    position: relative;
    z-index: 999999;
    pointer-events: none;
  `;
  
  shadowRoot.appendChild(container);
  
  return { shadowRoot, container };
};

export const injectStylesIntoShadowDOM = (shadowRoot: ShadowRoot, additionalStyles?: string[]) => {
  // Inject Material-UI and other necessary styles
  const materialUIStyles = `
    /* Material-UI Reset */
    *, *::before, *::after {
      box-sizing: border-box;
    }
    
    /* Ensure proper font rendering */
    body {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    
    /* FIXED: Ensure Shadow DOM container doesn't block interactions */
    #chat-widget-shadow-container {
      pointer-events: none;
    }
    
    /* FIXED: Allow widget components to receive pointer events */
    [data-widget-component] {
      pointer-events: auto;
    }
    
    /* Fix for Material-UI components */
    .MuiBox-root {
      box-sizing: border-box;
    }
    
    .MuiFab-root {
      box-sizing: border-box;
    }
    
    .MuiTooltip-root {
      box-sizing: border-box;
    }
    
    /* Ensure proper z-index stacking */
    .MuiModal-root {
      z-index: 1300 !important;
    }
    
    .MuiTooltip-popper {
      z-index: 1500 !important;
    }
  `;
  
  // Create and inject the base styles
  const baseStyleElement = document.createElement('style');
  baseStyleElement.textContent = materialUIStyles;
  shadowRoot.appendChild(baseStyleElement);
  
  // Inject additional styles if provided
  if (additionalStyles && additionalStyles.length > 0) {
    additionalStyles.forEach(styleText => {
      const styleElement = document.createElement('style');
      styleElement.textContent = styleText;
      shadowRoot.appendChild(styleElement);
    });
  }
  
  // Try to inject existing document styles (for development)
  try {
    const styleSheets = Array.from(document.styleSheets);
    styleSheets.forEach((sheet) => {
      try {
        const cssRules = Array.from(sheet.cssRules || sheet.rules || []);
        const cssText = cssRules.map(rule => rule.cssText).join('\n');
        
        if (cssText.trim()) {
          const styleElement = document.createElement('style');
          styleElement.textContent = cssText;
          shadowRoot.appendChild(styleElement);
        }
      } catch (error) {
        // Handle CORS errors for external stylesheets
        console.warn('Could not inject external stylesheet into Shadow DOM:', error);
      }
    });
  } catch (error) {
    console.warn('Could not access document stylesheets:', error);
  }
};

export const isShadowDOMSupported = (): boolean => {
  return typeof Element !== 'undefined' && 
         typeof Element.prototype.attachShadow === 'function';
};

export const safePostMessage = (message: any, targetOrigin: string = '*') => {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, targetOrigin);
    }
  } catch (error) {
    console.warn('Could not send postMessage:', error);
  }
};

export const safeDocumentStyleModification = (cssText: string) => {
  try {
    // Only modify document styles if we're not in Shadow DOM
    if (!document.querySelector('#chat-widget-shadow-container')) {
      document.documentElement.style.cssText = cssText;
    }
  } catch (error) {
    console.warn('Could not modify document styles:', error);
  }
};

export function createEmotionCacheForShadowRoot(shadowRoot: ShadowRoot) {
  return createCache({
    key: 'mui-shadow',
    container: shadowRoot,
    prepend: true,
  });
} 